/**
 * Set Sentry context (tags, user) for filtering and dashboards.
 * Call after auth and when tenant/route changes. No PII in tags.
 */

import * as Sentry from "@sentry/nextjs";
import { SENTRY_PROJECT, SENTRY_TAGS } from "./constants";

export type SentryContextParams = {
  tenantId?: string | null;
  userRole?: string | null;
  userId?: string | null;
  /**
   * ATT-2872: normalized hostname of the request (e.g. `admin.demo.attendly.com`).
   * Low cardinality; no user PII. Lets Sentry filters separate errors by
   * subdomain even when multiple subdomains target the same backend tenant.
   */
  domain?: string | null;
  /**
   * ATT-2872: tenant slug derived from the resolved tenant config or
   * discovery store (e.g. `demo`, `meetenrich`, `sausd`). Low cardinality;
   * no user PII.
   */
  tenantSlug?: string | null;
};

/**
 * Set global tags and user for the current scope.
 * Use opaque IDs only (no email, name, tokens).
 */
export function setSentryContext(params: SentryContextParams): void {
  Sentry.setTag(SENTRY_TAGS.project, SENTRY_PROJECT);

  if (params.tenantId) {
    Sentry.setTag(SENTRY_TAGS.tenantId, params.tenantId);
  }
  if (params.userRole) {
    Sentry.setTag(SENTRY_TAGS.userRole, params.userRole);
  }
  if (params.domain) {
    Sentry.setTag(SENTRY_TAGS.domain, params.domain);
  }
  if (params.tenantSlug) {
    Sentry.setTag(SENTRY_TAGS.tenantSlug, params.tenantSlug);
  }
  if (params.userId) {
    Sentry.setUser({ id: params.userId });
  }
}

/**
 * Set the current action (short label) for the next capture.
 * E.g. "checkout.submit", "enrollment.list", "login".
 */
export function setSentryAction(action: string): void {
  Sentry.setTag(SENTRY_TAGS.action, action);
}
